// Packages
import { Button, Icon, IconButton } from "@jkast90/kast-common/ui";
import { createPortal } from "react-dom";

// Styling
import styles from "./ReminderDrawer.module.css";
import { useNow } from "@jkast90/kast-common/format/react";
import { toDate, type ShownReminder } from "./reminderShape";

/**
 * Format a due date into time if today, else full date
 * @param {string} iso - ISO string
 * @returns {string}
 */
function formatDueDisplay(iso: ShownReminder["due" | "completed"]) {
  const due = toDate(iso);
  const now = new Date();
  const isToday = due.toDateString() === now.toDateString();
  return isToday
    ? due.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : due.toLocaleDateString();
}

export default function ReminderDrawer<R extends ShownReminder>({
  reminders = [],
  onSnooze,
  onComplete,
}: {
  reminders?: R[];
  onSnooze: (reminder: R, minutes: number) => void;
  onComplete: (reminder: R) => void;
}) {
  const now = useNow();

  const active = reminders
    .filter((r) => !r.completed && toDate(r.due).getTime() > now)
    .sort((a, b) => toDate(a.due).getTime() - toDate(b.due).getTime());

  const completed = reminders
    .filter((r) => r.completed)
    .sort(
      (a, b) => toDate(b.completed).getTime() - toDate(a.completed).getTime(),
    )
    .slice(0, 10); // most recent 10

  return createPortal(
    <div className={styles.drawer}>
      <h3>Reminders</h3>
      <div className={styles.section}>
        {active.map((r) => (
          <div key={r.id || r.due} className={styles.activeItem}>
            <div>
              <strong>{r.note?.title || "(Untitled Note)"}</strong>
              <br />
              <small>Due: {formatDueDisplay(r.due)}</small>
            </div>
            <div className={styles.actions}>
              <IconButton
                size="sm"
                variant="primary"
                label="Mark done"
                onClick={() => onComplete(r)}
              >
                <Icon name="check" size={16} />
              </IconButton>
              <Button
                size="sm"
                variant="primary"
                onClick={() => onSnooze(r, 30)}
              >
                Snooze 30m
              </Button>
            </div>
          </div>
        ))}
      </div>

      {completed.length > 0 && (
        <>
          <h4>History</h4>
          <div className={styles.section}>
            {completed.map((r) => (
              <div
                key={String(r.id || r.completed)}
                className={styles.historyItem}
              >
                <div>
                  <span>{r.note?.title || "(Untitled Note)"}</span>
                  <br />
                  <small>Completed: {formatDueDisplay(r.completed)}</small>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
